import { useEffect, useRef, useState } from "react";
import { useVideoControl } from "../contexts/VideoControlContext";

interface UseSynchronizedVideoProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  videoId: string;
  videoIndex: number;
  enabled?: boolean;
  isLive?: boolean;
  getForwardBufferEnd?: () => number | null;
  onSeek?: (target: number) => void;
}

export const useSynchronizedVideo = ({
  videoRef,
  videoId,
  videoIndex,
  enabled = true,
  isLive = false,
  getForwardBufferEnd,
  onSeek,
}: UseSynchronizedVideoProps) => {
  const { state, actions } = useVideoControl();
  const actionsRef = useRef(actions);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );

  useEffect(() => {
    actionsRef.current = actions;
  });

  useEffect(() => {
    if (!enabled) return;

    const checkVideo = () => {
      // Tracks ref changes in both directions. When SynchronizedHlsVideo
      // replaces the <video> with a fatal-error <Alert>, videoRef.current goes
      // null — clearing the state here triggers the main effect's cleanup so the
      // tile unregisters and stops being counted by the buffer governor.
      if (videoRef.current !== videoElement) {
        setVideoElement(videoRef.current);
      }
    };

    checkVideo();
    const interval = setInterval(checkVideo, 50);

    return () => {
      clearInterval(interval);
    };
  }, [videoRef, videoElement, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const video = videoElement;
    if (!video) return;

    actionsRef.current.registerVideoRef(videoId, video, {
      videoIndex,
      isLive,
      getForwardBufferEnd,
      onSeek,
    });

    const reportDuration = () => {
      if (isLive) return;
      if (video.duration && Number.isFinite(video.duration)) {
        actionsRef.current.updateDuration(video.duration);
      }
    };

    video.addEventListener("durationchange", reportDuration);
    video.addEventListener("loadedmetadata", reportDuration);
    reportDuration();

    return () => {
      video.removeEventListener("durationchange", reportDuration);
      video.removeEventListener("loadedmetadata", reportDuration);
      actionsRef.current.unregisterVideoRef(videoId);
    };
  }, [
    videoId,
    videoIndex,
    videoElement,
    isLive,
    getForwardBufferEnd,
    onSeek,
    enabled,
  ]);

  return {
    currentTime: state.currentTime,
    duration: state.duration,
    isPlaying: state.isPlaying,
    playbackRate: state.playbackRate,
    isBuffering: state.isBuffering,
  };
};
